import { IntlShape } from "react-intl";
import { TFunction } from "i18next";
import { formatDateLocale, formatNumberLocale, toTitleCase } from "@/utils/helpers";
import { TableColumnHeader } from "@/components/shared/table/Table";

/**
 * Generates column definitions for the billing table.
 *
 * @param intl - Internationalization object for formatting
 * @param t - Translation function
 * @returns Array of column definitions for the DataTable
 */
export const getColumnsBillings = (intl: IntlShape, t: TFunction) => {
  return [
    {
      accessorKey: "subscriptionStatus",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.subscriptionstatus")}
          column={column}
        />
      ),
      cell: ({ row }) => toTitleCase(row.original.subscriptionStatus),
    },
    {
      accessorKey: "organization.shortName",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.customername")}
          column={column}
        />
      ),
      cell: ({ row }) => row.original.organization.shortName ?? "",
    },
    {
      accessorKey: "pricePerUser",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.priceperuser")}
          column={column}
          align="end"
        />
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumberLocale(
            intl,
            row.original.pricePerUser ? row.original.pricePerUser / 100 : 0,
            "currency"
          )}
        </div>
      ),
    },
    {
      accessorKey: "currentSeatCount",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.currentseatcount")}
          column={column}
          align="end"
        />
      ),
      cell: ({ row }) => (
        <div className="text-right">{row.original.currentSeatCount}</div>
      ),
    },
    {
      accessorKey: "billingEmail",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.billingemail")}
          column={column}
        />
      ),
      cell: ({ row }) => row.original.billingEmail ?? "",
    },
    {
      accessorKey: "collectionMethod",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.collectionmethod")}
          column={column}
        />
      ),
      cell: ({ row }) =>
        row.original.collectionMethod
          ? toTitleCase(row.original.collectionMethod.replace(/_/g, " "))
          : "",
    },
    {
      accessorKey: "latestInvoiceStatus",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.latestinvoicestatus")}
          column={column}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {toTitleCase(row.original.latestInvoiceStatus ?? "")}
        </div>
      ),
    },
    {
      accessorKey: "paidThrough",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.paidthrough")}
          column={column}
        />
      ),
      cell: ({ row }) => formatDateLocale(intl, row.original.paidThrough),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.createdat")}
          column={column}
        />
      ),
      cell: ({ row }) => formatDateLocale(intl, row.original.createdAt),
    },
    {
      accessorKey: "updatedAt",
      header: ({ column }) => (
        <TableColumnHeader
          text={t("tables:billings.columns.updatedat")}
          column={column}
        />
      ),
      cell: ({ row }) => formatDateLocale(intl, row.original.updatedAt),
    },
  ];
};
